import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDatasetStatus, pauseDatasetJob, resumeDatasetJob, runDatasetJob, updateDatasetJob,
  setDatasetSession, setDatasetAutoSession, getDatasetQuality, refreshDatasetQuality,
} from '../api';
import { lionAutoKeys } from './useLionAuto';
import { backfillIds, autoIds } from '../utils/datasetStatus';

const LION_AUTO_STATUS_KEY = lionAutoKeys.autoStatus;

// Admin → Data (super admin only): backfill jobs, the backfill Skybox logins,
// Lion Auto's Skybox logins, the data-quality report.
export const datasetKeys = {
  all: ['dataset'],
  status: ['dataset', 'status'],
  quality: ['dataset', 'quality'],
};

// 503 BACKFILL_UNAVAILABLE = no backfill engine on this server (yet): not
// worth retrying, the tab shows a muted note instead
export const isBackfillUnavailable = (err) => err?.response?.status === 503;
const retryUnlessUnavailable = (count, err) => !isBackfillUnavailable(err) && count < 1;

// Polls every 10 s while the tab is mounted and the window visible (React
// Query skips interval refetches in a background tab). Stops while the server
// has no backfill engine (503); the tab offers a manual "Check again".
export function useDatasetStatus(options = {}) {
  return useQuery({
    queryKey: datasetKeys.status,
    queryFn: async () => (await getDatasetStatus()).data,
    refetchInterval: (query) => (isBackfillUnavailable(query.state.error) ? false : 10000),
    retry: retryUnlessUnavailable,
    ...options,
  });
}

// The stored snapshot only; the server never recomputes on a read
export function useDatasetQuality(options = {}) {
  return useQuery({
    queryKey: datasetKeys.quality,
    queryFn: async () => (await getDatasetQuality()).data,
    staleTime: 30000,
    refetchInterval: 60000,
    retry: 1,
    ...options,
  });
}

// A job change answers with the fresh status, a session change with the
// session list (the backfill logins' answer adds dedicatedUserIds, Lion
// Auto's adds autoUserIds): write it into the cache, keeping whatever the
// answer doesn't carry, then refetch to be sure. A failure refetches too: a
// change that failed part-way (Lion Auto's logins) may have saved some of it
function useStatusMutation(mutationFn, { also = [] } = {}) {
  const qc = useQueryClient();
  const refetch = () => {
    qc.invalidateQueries({ queryKey: datasetKeys.status });
    for (const queryKey of also) qc.invalidateQueries({ queryKey });
  };
  return useMutation({
    mutationFn,
    onSuccess: (res) => {
      const d = res?.data || {};
      if (d.status && typeof d.status === 'object') {
        qc.setQueryData(datasetKeys.status, d.status);
      } else if (Array.isArray(d.sessions)) {
        const patch = { sessions: d.sessions };
        if (Array.isArray(d.dedicatedUserIds) || Number.isInteger(d.dedicatedUserId)) {
          const ids = backfillIds(d);
          patch.dedicatedUserIds = ids;
          patch.dedicatedUserId = ids[0] ?? null;
        }
        if (Array.isArray(d.autoUserIds)) {
          const ids = autoIds(d);
          patch.autoUserIds = ids;
          patch.autoUserId = ids[0] ?? null;
        }
        qc.setQueryData(datasetKeys.status, (old) => (old ? { ...old, ...patch } : old));
      }
      refetch();
    },
    onError: refetch,
  });
}

export const usePauseDatasetJob = () => useStatusMutation((job) => pauseDatasetJob(job));
export const useResumeDatasetJob = () => useStatusMutation((job) => resumeDatasetJob(job));
export const useRunDatasetJob = () => useStatusMutation((job) => runDatasetJob(job));
export const useUpdateDatasetJob = () => useStatusMutation(({ job, ...body }) => updateDatasetJob(job, body));
export const useSetDatasetSession = () => useStatusMutation((userIds) => setDatasetSession(userIds));
// Lion Auto's logins (variables: the whole list of user ids, [] clears
// them). The runner's status lists those logins, so it re-reads too.
export const useSetDatasetAutoSession = () => useStatusMutation(
  (userIds) => setDatasetAutoSession(userIds),
  { also: [LION_AUTO_STATUS_KEY] },
);

export function useRefreshDatasetQuality() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => refreshDatasetQuality(),
    onSuccess: (res) => {
      if (res?.data?.snapshot) qc.setQueryData(datasetKeys.quality, { snapshot: res.data.snapshot, refresh: res.data.refresh || null });
    },
  });
}
